import React from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import { PERMISSIONS } from '../../../../helpers/auth';
import { FAILURE_TYPES } from '../../config/constants';
import i18n from '../../../../config/i18n';
import { FilterMatchMode } from 'primereact/api';
import {
  dateRangeFilterTemplate,
  dateRangeFilterFunction,
  singleSelectFilterTemplate,
  textFilterTemplate,
} from '../../../../components/shared/DataTable/filterComponents';

export const ROUTES = [
  {
    label: 'failure_notification_title',
    link: '',
  },
  {
    label: 'additional_functionalities',
    link: 'additional-functionalities',
  },
];

export const COLUMNS = ({ t, navigate, hasRole }) => [
  {
    label: 'failure_notification.reference_number',
    value: 'failureReferenceNumber',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('failure_notification.reference_number') }),
      ),
  },
  {
    label: 'failure_notification.technical_failure_type',
    value: 'technicalFailureType',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(
        options,
        FAILURE_TYPES({ lang: i18n.language }),
      ),
    filterValueFormatter: (value) =>
      FAILURE_TYPES({ lang: i18n.language }).find((v) => v.value === value)
        ?.label || value,
    body: (rowData) =>
      FAILURE_TYPES({ lang: i18n.language }).find(
        (v) => v.value === rowData.technicalFailureType,
      )?.label,
  },
  {
    label: 'failure_notification.columns.solution_provider_name',
    value: 'solutionProviderName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', {
          column: t('failure_notification.solution_provider_name'),
        }),
      ),
  },
  {
    label: 'failure_notification.columns.technical_failure_date',
    value: 'technicalFailureDate',
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
    body: (rowData) => {
      if (!rowData?.technicalFailureDate) {
        return '-';
      }
      const d = moment(rowData.technicalFailureDate, 'YYYY-MM-DD hh:mm:ss');
      return `${d.format('DD')} ${d
        .clone()
        .locale(i18n.language)
        .format('MMM')} ${d.format('YYYY')}`;
    },
    sortable: false,
  },
  {
    label: 'failure_notification.columns.resolution_date',
    value: 'resolution.resolutionDate',
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
    body: (rowData) => {
      if (!rowData?.resolution?.resolutionDate) {
        return '-';
      }
      const d = moment(
        rowData.resolution.resolutionDate,
        'YYYY-MM-DD hh:mm:ss',
      );
      return `${d.format('DD')} ${d
        .clone()
        .locale(i18n.language)
        .format('MMM')} ${d.format('YYYY')}`;
    },
    sortable: false,
  },
  {
    label: 'failure_notification.status',
    value: 'status',
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, [
        {
          label: t('failure_notification.OPEN'),
          value: 'OPEN',
        },
        {
          label: t('failure_notification.RESOLVED'),
          value: 'RESOLVED',
        },
      ]),
    filterValueFormatter: (value) => t(`failure_notification.${value}`),
    body: (rowData) => {
      const color =
        STATUS_COLOR_MAP[rowData?.status] || STATUS_COLOR_MAP.DEFAULT;
      return (
        <span
          style={{
            color,
          }}
        >
          {t(`failure_notification.${rowData?.status}`)}
        </span>
      );
    },
  },
  {
    label: 'failure_notification.actions',
    body: (rowData) => {
      return (
        <Button
          style={{ minWidth: '100px' }}
          outlined={rowData?.status === 'RESOLVED'}
          disabled={
            rowData.status === 'OPEN' && !hasRole(PERMISSIONS.RESOLVE_FAILURE)
          }
          onClick={() =>
            rowData.status === 'OPEN'
              ? navigate(`resolve/${rowData.failureReferenceNumber}`, {
                  state: { data: rowData, readOnly: false },
                })
              : navigate(`${rowData.failureReferenceNumber}`, {
                  state: {
                    data: rowData,
                    id: rowData.failureReferenceNumber,
                    readOnly: true,
                  },
                })
          }
          label={
            rowData?.status === 'OPEN'
              ? t('failure_notification.resolve')
              : t('failure_notification.view')
          }
        />
      );
    },
  },
];

const STATUS_COLOR_MAP = {
  RESOLVED: '#00AC28',
  OPEN: '#D36D00',
};

export const SEARCH_CONFIG = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  failureReferenceNumber: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  technicalFailureType: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  solutionProviderName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  technicalFailureDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  'resolution.resolutionDate': {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  status: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
};
